import React, {useState, useEffect, useRef} from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Paper,
	Typography,
	TextField,
	Checkbox,
	FormControlLabel,
	Alert,
} from '@mui/material';
import {
	CloudUpload as UploadIcon,
	Collections as GalleryIcon,
	Search as SearchIcon,
	Clear as ClearIcon,
	CheckCircle as CheckCircleIcon,
	Delete as DeleteIcon,
	Visibility as ViewIcon,
	Crop as CropIcon,
	Close,
} from '@mui/icons-material';
import {toast} from 'react-toastify';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import BasicModal from 'src/components/modals/BasicModal';

// Import utilities
import {uploadFileRequest} from 'src/utils/AdminApi';
import {
	validateFileSize,
	validateFileType,
	getCompressedFile,
} from 'src/components/inputs/ImageCropPicker.js/filesUtils';
import {CloseButton} from './ProductImagePreview';

const Gallery = ({
	open,
	onClose: _onClose,
	onSelect,
	onChange,
	allowMultiple = false,
	selectedFiles = [],
	availableFiles = [], // New prop for available files
	onFileUpload, // Callback when files are uploaded
	onFileDelete, // Callback when files are deleted
	maxFiles = 10,
	maxSizeInMB = 5,
	fileTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
	title = 'معرض الملفات',
	showDeleteButton = false, // Whether to show delete buttons
	// Cropping options
	enableCrop = false, // Whether to enable cropping
	forceCrop = false, // Whether to force cropping (show crop modal always)
	readOnly = false, // Whether to force cropping (show crop modal always)
	cropRatio = 4 / 5, // Aspect ratio for cropping
}) => {
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedLocalFiles, setSelectedLocalFiles] = useState(selectedFiles);
	const [didAutoShow, setDidAutoShow] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState([]);
	const [viewingFile, setViewingFile] = useState(null);
	const fileInputRef = useRef(null);

	// Cropping states
	const [cropModalOpen, setCropModalOpen] = useState(false);
	const [selectedImageForCrop, setSelectedImageForCrop] = useState(null);
	const [filesToCrop, setFilesToCrop] = useState([]);
	const [currentCropIndex, setCurrentCropIndex] = useState(0);
	const cropperRef = useRef(null);

	const onClose = () => {
		setDidAutoShow(false);
		_onClose();
	};

	// Reset selection when dialog opens/closes and auto-trigger file selection if no files available
	useEffect(() => {
		if (readOnly) return;
		if (didAutoShow) return;
		if (open) {
			setSelectedLocalFiles(selectedFiles);

			// Auto-open file selection dialog if no available files
			if (availableFiles.length === 0) {
				// Small delay to ensure the dialog is fully rendered
				setTimeout(() => {
					fileInputRef.current?.click();
				}, 100);
				setDidAutoShow(true);
			}
		}
	}, [open, selectedFiles, availableFiles.length]);

	// Handle file upload
	const handleFileUpload = async (files) => {
		if (readOnly) return;
		const fileArray = Array.from(files);

		// Validate files
		const validFiles = [];
		for (const file of fileArray) {
			if (!validateFileType(file)) {
				toast.error(`نوع الملف غير مدعوم: ${file.name}`);
				continue;
			}
			if (!validateFileSize(file, maxSizeInMB)) {
				toast.error(`حجم الملف كبير جداً: ${file.name}`);
				continue;
			}
			validFiles.push(file);
		}

		if (validFiles.length === 0) return;

		// Check if cropping is enabled and files are images
		const imageFiles = validFiles.filter((file) => file.type.startsWith('image/'));
		const shouldShowCrop = enableCrop && imageFiles.length > 0;

		if (forceCrop) {
			// Show crop modal for image files
			setFilesToCrop(imageFiles);
			setCurrentCropIndex(0);
			setSelectedImageForCrop(URL.createObjectURL(imageFiles[0]));
			setCropModalOpen(true);
			return;
		}

		// Direct upload without cropping
		await uploadFiles(validFiles);
	};

	// Upload files directly (used for non-image files or when cropping is disabled)
	const uploadFiles = async (validFiles) => {
		setUploading(true);
		const progressArray = validFiles.map(() => 0);
		setUploadProgress(progressArray);

		try {
			const uploadPromises = validFiles.map(async (file, index) => {
				try {
					const compressedFile = await getCompressedFile(file);
					const response = await uploadFileRequest(compressedFile);
					progressArray[index] = 100;
					setUploadProgress([...progressArray]);
					return response[0];
				} catch (error) {
					toast.error(`فشل في رفع الملف: ${file.name}`);
					return null;
				}
			});

			const results = await Promise.all(uploadPromises);
			const successfulUploads = results.filter(Boolean);

			if (successfulUploads.length > 0) {
				toast.success(`تم رفع ${successfulUploads.length} ملف بنجاح`);
				// Call the upload callback to update the parent component
				if (onFileUpload) {
					onFileUpload(successfulUploads);
				}

				// Auto-select the newly uploaded files
				if (allowMultiple) {
					// For multiple selection, add new files to existing selection (respecting maxFiles limit)
					setSelectedLocalFiles((prev) => {
						const newSelection = [...prev];
						const availableSlots = maxFiles - newSelection.length;
						const filesToAdd = successfulUploads.slice(0, availableSlots);
						return [...newSelection, ...filesToAdd];
					});
				} else {
					// For single selection, select the first uploaded file
					setSelectedLocalFiles([successfulUploads[0]]);
				}
			}
		} catch (error) {
			toast.error('حدث خطأ أثناء رفع الملفات');
		} finally {
			setUploading(false);
			setUploadProgress([]);
		}
	};

	// Handle cropping existing file
	const handleCropExistingFile = (file) => {
		if (!file.mime?.startsWith('image/')) {
			toast.warning('يمكن قص الصور فقط');
			return;
		}

		// Set up for cropping existing file
		setFilesToCrop([file]);
		setCurrentCropIndex(0);
		setSelectedImageForCrop(file.url);
		setCropModalOpen(true);
	};

	// Handle crop confirmation
	const handleCropConfirm = async () => {
		if (!cropperRef.current) return;

		const croppedCanvas = cropperRef.current.cropper.getCroppedCanvas();
		setUploading(true);

		const currentFile = filesToCrop[currentCropIndex];
		const isExistingFile = currentFile && currentFile.id; // Existing files have IDs

		croppedCanvas.toBlob(
			async (blob) => {
				try {
					const response = await uploadFileRequest(blob);
					const uploadedFile = response[0];

					if (isExistingFile) {
						// Cropping existing file - notify parent about replacement
						if (onFileUpload) {
							onFileUpload([uploadedFile]);
						}

						// Update selection if the cropped file was selected
						if (selectedLocalFiles.some((f) => f.id === currentFile.id)) {
							setSelectedLocalFiles((prev) =>
								prev.map((f) => (f.id === currentFile.id ? uploadedFile : f)),
							);
						}

						toast.success('تم قص الصورة وحفظها بنجاح');
					} else {
						// Cropping new file - standard upload flow
						if (onFileUpload) {
							onFileUpload([uploadedFile]);
						}

						// Auto-select the uploaded file
						if (allowMultiple) {
							setSelectedLocalFiles((prev) => {
								const availableSlots = maxFiles - prev.length;
								if (availableSlots > 0) {
									return [...prev, uploadedFile];
								}
								return prev;
							});
						} else {
							setSelectedLocalFiles([uploadedFile]);
						}
					}

					// Move to next file or close modal
					const nextIndex = currentCropIndex + 1;
					if (nextIndex < filesToCrop.length) {
						setCurrentCropIndex(nextIndex);
						const nextFile = filesToCrop[nextIndex];
						const nextImageSrc = nextFile.url || URL.createObjectURL(nextFile);
						setSelectedImageForCrop(nextImageSrc);
					} else {
						// All files processed
						setCropModalOpen(false);
						setFilesToCrop([]);
						setCurrentCropIndex(0);
						setSelectedImageForCrop(null);
						if (!isExistingFile) {
							toast.success('تم رفع جميع الصور بنجاح');
						}
					}
				} catch (error) {
					toast.error('فشل في رفع الصورة');
				} finally {
					setUploading(false);
				}
			},
			'image/webp',
			0.9,
		);
	};

	// Handle file selection
	const handleFileSelect = (file) => {
		if (readOnly) return setViewingFile(file);
		if (allowMultiple) {
			const isSelected = selectedLocalFiles.some((f) => f.id === file.id);
			if (isSelected) {
				setSelectedLocalFiles((prev) => prev.filter((f) => f.id !== file.id));
			} else {
				if (selectedLocalFiles.length < maxFiles) {
					setSelectedLocalFiles((prev) => [...prev, file]);
				} else {
					toast.warning(`لا يمكن اختيار أكثر من ${maxFiles} ملفات`);
				}
			}
		} else {
			setSelectedLocalFiles([file]);
		}
	};

	// Handle file deletion
	const handleFileDelete = async (file) => {
		try {
			// Call the delete callback to let parent handle the deletion
			if (onFileDelete) {
				await onFileDelete(file);
				toast.success('تم حذف الملف بنجاح');
			} else {
				toast.warning('حذف الملفات غير مدعوم في هذا المعرض');
			}
		} catch (error) {
			toast.error('فشل في حذف الملف');
		}
	};

	// Handle selection confirmation
	const handleConfirm = () => {
		onSelect(allowMultiple ? selectedLocalFiles : selectedLocalFiles[0]);
		onClose();
	};

	// Filter files based on search term
	const filteredFiles = availableFiles.filter(
		(file) =>
			file.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
			file.alternativeText?.toLowerCase().includes(searchTerm.toLowerCase()),
	);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="lg"
			fullWidth
			PaperProps={{
				sx: {height: '80vh'},
			}}
		>
			<DialogTitle>
				<Box display="flex" alignItems="center" gap={1}>
					<GalleryIcon />
					<Typography variant="h6">{title}</Typography>
				</Box>
			</DialogTitle>

			<DialogContent dividers>
				{/* Upload Area - show prominently when no files available */}
				{filteredFiles.length === 0 ? (
					<Box>
						<input
							type="file"
							multiple
							ref={fileInputRef}
							disabled={readOnly}
							style={{display: 'none'}}
							accept={fileTypes.join(',')}
							onChange={(e) => handleFileUpload(e.target.files)}
						/>

						{/* Primary upload area when no files */}
						<Paper
							sx={{
								border: '2px dashed',
								borderColor: uploading ? 'primary.main' : 'primary.light',
								borderRadius: 2,
								p: 6,
								textAlign: 'center',
								cursor: uploading ? 'not-allowed' : 'pointer',
								bgcolor: uploading ? 'grey.50' : 'primary.50',
								'&:hover': {
									borderColor: uploading ? 'primary.main' : 'primary.main',
									bgcolor: uploading ? 'grey.50' : 'primary.100',
								},
							}}
							onClick={() => !uploading && !readOnly && fileInputRef.current?.click()}
							onDrop={(e) => {
								e.preventDefault();
								if (!uploading) {
									handleFileUpload(e.dataTransfer.files);
								}
							}}
							onDragOver={(e) => e.preventDefault()}
						>
							{uploading ? (
								<Box>
									<CircularProgress sx={{mb: 2}} />
									<Typography variant="h6">جاري رفع الملفات...</Typography>
									{uploadProgress.map((progress, index) => (
										<Box key={index} sx={{mt: 1}}>
											<Typography variant="caption">
												ملف {index + 1}: {progress}%
											</Typography>
										</Box>
									))}
								</Box>
							) : !readOnly ? (
								<Box>
									<UploadIcon sx={{fontSize: 80, color: 'primary.main', mb: 2}} />
									<Typography variant="h5" gutterBottom color="primary">
										اسحب الملفات هنا أو اضغط للاختيار
									</Typography>
									<Typography variant="body1" color="grey.600" sx={{mb: 2}}>
										لا توجد ملفات متاحة حالياً. ابدأ برفع أول ملف
									</Typography>
									<Typography variant="body2" color="grey.600">
										الحد الأقصى: {maxSizeInMB} ميجابايت لكل ملف
									</Typography>
									<Typography variant="caption" color="grey.500" display="block" mt={1}>
										الأنواع المدعومة: {fileTypes.map((type) => type.split('/')[1]).join(', ')}
									</Typography>
								</Box>
							) : (
								<Box>
									<Typography variant="h6" color="grey.600" sx={{mb: 2}}>
										لا توجد ملفات متاحة للعرض حالياً.
									</Typography>
								</Box>
							)}
						</Paper>
					</Box>
				) : (
					<Box>
						{/* Search */}
						{/* <Box sx={{mb: 2}}>
							<TextField
								fullWidth
								placeholder="البحث في الملفات..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								InputProps={{
									startAdornment: <SearchIcon sx={{mr: 1}} />,
									endAdornment: searchTerm && (
										<IconButton size="small" onClick={() => setSearchTerm('')}>
											<ClearIcon />
										</IconButton>
									),
								}}
							/>
						</Box> */}

						{/* Upload area - compact when files exist */}
						{!readOnly && (
							<Paper
								sx={{
									border: '1px dashed',
									borderColor: 'grey.300',
									borderRadius: 1,
									p: 2,
									mb: 2,
									textAlign: 'center',
									cursor: uploading ? 'not-allowed' : 'pointer',
									'&:hover': {
										borderColor: uploading ? 'grey.300' : 'primary.main',
										bgcolor: uploading ? 'transparent' : 'grey.50',
									},
								}}
								onClick={() => !uploading && !readOnly && fileInputRef.current?.click()}
								onDrop={(e) => {
									e.preventDefault();
									if (!uploading) {
										handleFileUpload(e.dataTransfer.files);
									}
								}}
								onDragOver={(e) => e.preventDefault()}
							>
								<input
									disabled={readOnly}
									type="file"
									multiple
									ref={fileInputRef}
									style={{display: 'none'}}
									accept={fileTypes.join(',')}
									onChange={(e) => handleFileUpload(e.target.files)}
								/>
								{!readOnly && (
									<>
										{uploading ? (
											<Box display="flex" alignItems="center" justifyContent="center" gap={2}>
												<CircularProgress size={20} />
												<Typography variant="body2">جاري رفع الملفات...</Typography>
											</Box>
										) : (
											<Box display="flex" alignItems="center" justifyContent="center" gap={1}>
												<UploadIcon sx={{fontSize: 24}} />
												<Typography variant="body2">اسحب ملفات جديدة هنا أو اضغط لاختيار ملفات</Typography>
											</Box>
										)}
									</>
								)}
							</Paper>
						)}

						{/* Selected files info */}
						{selectedLocalFiles.length > 0 && !readOnly && (
							<Alert severity="info" sx={{mb: 2}}>
								تم اختيار {selectedLocalFiles.length} {allowMultiple ? 'ملفات' : 'ملف'}
								{allowMultiple && ` من أصل ${maxFiles} ملفات كحد أقصى`}
							</Alert>
						)}

						{/* Files grid */}
						<Grid container spacing={2}>
							{filteredFiles.map((file) => {
								const isSelected = selectedLocalFiles.some((f) => f.id === file.id);
								const isImage = file.mime?.startsWith('image/');

								return (
									<Grid item xs={6} sm={4} md={3} key={file.id}>
										<Paper
											sx={{
												position: 'relative',
												cursor: 'pointer',
												border: isSelected ? 2 : 1,
												borderColor: isSelected ? 'primary.main' : 'grey.300',
												borderRadius: 1,
												overflow: 'hidden',
												'&:hover': {
													boxShadow: 2,
												},
											}}
											onClick={() => handleFileSelect(file)}
										>
											{/* File preview */}
											<Box
												sx={{
													width: '100%',
													height: 150,
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													bgcolor: 'grey.100',
												}}
											>
												{isImage ? (
													<img
														src={file.url}
														alt={file.name}
														style={{
															width: '100%',
															height: '100%',
															objectFit: 'cover',
														}}
													/>
												) : (
													<Typography variant="h4" color="grey.500">
														📄
													</Typography>
												)}
											</Box>

											{/* File info */}
											<Box p={1}>
												{/* <Typography variant="caption" display="block" noWrap>
													{file.name}
												</Typography> */}
												<Typography variant="caption" color="grey.500" display="block">
													{(file.size / 1024).toFixed(1)} KB
												</Typography>
											</Box>

											{/* Selection indicator */}
											{isSelected && !readOnly && (
												<CheckCircleIcon
													sx={{
														position: 'absolute',
														top: 8,
														right: 8,
														color: 'primary.main',
														bgcolor: 'white',
														borderRadius: '50%',
													}}
												/>
											)}

											{/* Action buttons */}
											<Box
												sx={{
													position: 'absolute',
													top: 8,
													left: 8,
													display: 'flex',
													gap: 0.5,
												}}
											>
												<IconButton
													size="small"
													sx={{bgcolor: 'rgba(255,255,255,0.8)'}}
													onClick={(e) => {
														e.stopPropagation();
														setViewingFile(file);
													}}
												>
													<ViewIcon fontSize="small" />
												</IconButton>
												{enableCrop && !readOnly && file.mime?.startsWith('image/') && (
													<IconButton
														size="small"
														sx={{bgcolor: 'rgba(255,255,255,0.8)'}}
														onClick={(e) => {
															e.stopPropagation();
															handleCropExistingFile(file);
														}}
													>
														<CropIcon fontSize="small" />
													</IconButton>
												)}
												{showDeleteButton && !readOnly && (
													<IconButton
														size="small"
														sx={{bgcolor: 'rgba(255,255,255,0.8)'}}
														onClick={(e) => {
															e.stopPropagation();
															handleFileDelete(file);
														}}
													>
														<DeleteIcon fontSize="small" />
													</IconButton>
												)}
											</Box>
										</Paper>
									</Grid>
								);
							})}
						</Grid>
					</Box>
				)}
			</DialogContent>

			<DialogActions>
				<Button onClick={onClose}>إغلاق</Button>
				{!readOnly && (
					<Button variant="contained" onClick={handleConfirm} disabled={selectedLocalFiles.length === 0}>
						اختيار {selectedLocalFiles.length > 0 && `(${selectedLocalFiles.length})`}
					</Button>
				)}
			</DialogActions>

			{/* File viewer dialog */}
			<Dialog open={!!viewingFile} onClose={() => setViewingFile(null)} maxWidth="md" fullWidth>
				{viewingFile && (
					<>
						{/* <DialogTitle>{viewingFile.name}</DialogTitle> */}
						<DialogContent dividers sx={{position: 'relative', minHeight: 300}}>
							<Box textAlign="center">
								{viewingFile.mime?.startsWith('image/') ? (
									<img
										src={viewingFile.url}
										alt={viewingFile.name}
										style={{
											maxWidth: '100%',
											maxHeight: '70vh',
											objectFit: 'contain',
										}}
									/>
								) : (
									<Box p={4}>
										<Typography variant="h4">📄</Typography>
										<Typography variant="h6" mt={2}>
											{viewingFile.name}
										</Typography>
										<Typography variant="body2" color="grey.600">
											{viewingFile.mime}
										</Typography>
										<Typography variant="body2" color="grey.600">
											{(viewingFile.size / 1024).toFixed(1)} KB
										</Typography>
									</Box>
								)}
							</Box>

							<CloseButton onClick={() => setViewingFile(null)} size="small">
								<Close />
							</CloseButton>
						</DialogContent>
					</>
				)}
			</Dialog>

			{/* Crop Modal */}
			<BasicModal isVisible={cropModalOpen} setVisibility={setCropModalOpen}>
				<Box>
					<Typography variant="h6" gutterBottom>
						قص الصورة ({currentCropIndex + 1} من {filesToCrop.length})
					</Typography>

					{selectedImageForCrop && (
						<Cropper
							movable={false}
							draggable={false}
							modal={false}
							style={{
								height: 300,
								width: '100%',
								marginBottom: 16,
							}}
							initialAspectRatio={cropRatio}
							aspectRatio={cropRatio}
							src={selectedImageForCrop}
							zoomable={false}
							viewMode={1}
							minCropBoxHeight={10}
							minCropBoxWidth={10}
							background={false}
							autoCropArea={1}
							checkOrientation={false}
							ref={cropperRef}
							guides
						/>
					)}

					<Typography variant="body2" color="grey.600" textAlign="center" mb={2}>
						يرجى تحديد أبعاد الصورة لكي تظهر بشكل مناسب في الموقع
					</Typography>

					<Box display="flex" gap={1}>
						<Button
							onClick={() => {
								setCropModalOpen(false);
								setFilesToCrop([]);
								setCurrentCropIndex(0);
								setSelectedImageForCrop(null);
							}}
							disabled={uploading}
						>
							إلغاء
						</Button>

						<Button fullWidth variant="contained" onClick={handleCropConfirm} disabled={uploading}>
							{uploading ? (
								<>
									<CircularProgress size={20} sx={{mr: 1}} />
									جاري الرفع...
								</>
							) : (
								`تأكيد ${
									filesToCrop.length > 1 && currentCropIndex < filesToCrop.length - 1 ? 'والتالي' : ''
								}`
							)}
						</Button>
					</Box>
				</Box>
			</BasicModal>
		</Dialog>
	);
};

export default Gallery;
